import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import type { IntegrationWithRawData } from "./useIntegrations";

interface FilterOption {
  value: string;
  label: string;
  isChecked: boolean;
}

export interface UseIntegrationsFiltersReturn {
  // Filter state
  searchValue: string;
  setSearchValue: (value: string) => void;
  showFilters: boolean;
  toggleFilters: () => void;
  connectionSource: string | null;
  connectionStatus: string | null;
  sortField: string;
  sortDirection: string;

  // Filter options
  connectionSourceOptions: FilterOption[];
  connectionStatusOptions: FilterOption[];

  // Display labels
  sourceDisplayLabel: string;
  statusDisplayLabel: string;

  // Handlers
  handleConnectionSourceChange: (
    selectedOptions?: Array<{
      value: string;
      label: string;
    }>,
  ) => void;
  handleConnectionStatusChange: (
    selectedOptions?: Array<{
      value: string;
      label: string;
    }>,
  ) => void;
  handleSortChange: (field: string, direction: string) => void;

  // Filtered data
  filteredIntegrations: IntegrationWithRawData[];
}

export function useIntegrationsFilters(
  integrationsData: IntegrationWithRawData[],
  integrationTypesData: FilterOption[],
): UseIntegrationsFiltersReturn {
  const intl = useIntl();

  // Filter state - these are shared across all instances
  // Using useState with function initializer to ensure single source of truth
  const [searchValue, setSearchValue] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [connectionSource, setConnectionSource] = useState<string | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>("createdDate");
  const [sortDirection, setSortDirection] = useState<string>("ASC");

  // Filter options state
  const [connectionSourceOptions, setConnectionSourceOptions] = useState<
    FilterOption[]
  >([]);
  const [connectionStatusOptions, setConnectionStatusOptions] = useState<
    FilterOption[]
  >([
    { value: "all", label: "All", isChecked: true },
    { value: "active", label: "Active", isChecked: false },
    { value: "failed", label: "Failed", isChecked: false },
    { value: "pending", label: "Pending", isChecked: false },
  ]);

  // Update connection source options when API data is loaded
  useEffect(() => {
    if (integrationTypesData.length > 0) {
      // Add "All" option at the beginning
      setConnectionSourceOptions([
        { value: "all", label: "All", isChecked: true },
        ...integrationTypesData,
      ]);
    }
  }, [integrationTypesData]);

  // Get display label for selected source options
  const sourceDisplayLabel = useMemo(() => {
    const selected = connectionSourceOptions.find((opt) => opt.isChecked);
    if (!selected || selected.value === "all") {
      return intl.formatMessage({
        id: "PNDeo8",
        defaultMessage: "Connection Source",
        description: "Connection source filter label",
      });
    }
    // Format: "Connection Source: AWS" or "Connection Source: Azure" etc.
    return intl.formatMessage(
      {
        id: "MTKeam",
        defaultMessage: "Connection Source: {source}",
        description: "Connection source filter display format",
      },
      { source: selected.label },
    );
  }, [connectionSourceOptions, intl]);

  // Get display label for selected status options
  const statusDisplayLabel = useMemo(() => {
    const selected = connectionStatusOptions.find((opt) => opt.isChecked);
    if (!selected || selected.value === "all") {
      return intl.formatMessage({
        id: "w4pE99",
        defaultMessage: "Connection Status",
        description: "Connection status filter label",
      });
    }
    // Format: "Connection Status: Active" or "Connection Status: Pending" etc.
    return intl.formatMessage(
      {
        id: "CLlTSf",
        defaultMessage: "Connection Status: {status}",
        description: "Connection status filter display format",
      },
      { status: selected.label },
    );
  }, [connectionStatusOptions, intl]);

  // Handlers
  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const handleConnectionSourceChange = (
    selectedOptions?: Array<{
      value: string;
      label: string;
    }>,
  ) => {
    // Update options with isChecked property based on selectedOptions
    setConnectionSourceOptions((prevOptions) =>
      prevOptions.map((option) => ({
        ...option,
        isChecked: selectedOptions
          ? selectedOptions.some(
              (selectedOption) => selectedOption.value === option.value,
            )
          : false,
      })),
    );
    // Update filter value - if "all" is selected, set to null to remove query param
    const options = selectedOptions || [];
    if (options.length > 0 && options[0].value === "all") {
      setConnectionSource(null);
    } else {
      setConnectionSource(options.length > 0 ? options[0].value : null);
    }
  };

  const handleConnectionStatusChange = (
    selectedOptions?: Array<{
      value: string;
      label: string;
    }>,
  ) => {
    // Update options with isChecked property based on selectedOptions
    setConnectionStatusOptions((prevOptions) =>
      prevOptions.map((option) => ({
        ...option,
        isChecked: selectedOptions
          ? selectedOptions.some(
              (selectedOption) => selectedOption.value === option.value,
            )
          : false,
      })),
    );
    // Update filter value - if "all" is selected, set to null to remove query param
    const options = selectedOptions || [];
    if (options.length > 0 && options[0].value === "all") {
      setConnectionStatus(null);
    } else {
      setConnectionStatus(options.length > 0 ? options[0].value : null);
    }
  };

  const handleSortChange = (field: string, direction: string) => {
    setSortField(field);
    setSortDirection(direction);
  };

  // Filter integrations based on search
  // Note: connectionSource and connectionStatus filtering is now done on the backend via API
  const filteredIntegrations = useMemo(() => {
    if (integrationsData.length === 0) {
      return [];
    }
    const searchLower = searchValue.trim().toLowerCase();

    return integrationsData.filter((integration) => {
      // Apply search filter (client-side since it's not a backend parameter)
      if (searchLower) {
        const name = integration.name?.toLowerCase() || "";
        const application = integration.application?.toLowerCase() || "";
        if (!name.includes(searchLower) && !application.includes(searchLower)) {
          return false;
        }
      }

      return true;
    });
  }, [integrationsData, searchValue]);

  return {
    // Filter state
    searchValue,
    setSearchValue,
    showFilters,
    toggleFilters,
    connectionSource,
    connectionStatus,
    sortField,
    sortDirection,

    // Filter options
    connectionSourceOptions,
    connectionStatusOptions,

    // Display labels
    sourceDisplayLabel,
    statusDisplayLabel,

    // Handlers
    handleConnectionSourceChange,
    handleConnectionStatusChange,
    handleSortChange,

    // Filtered data
    filteredIntegrations,
  };
}
